import type { RRRouteObject } from "src/router";
import { isArrayElement } from "src/types/utils";
import type { ElementOf } from "ts-essentials";

export const SpacesAccesses = [
  "accessible-from-limited-view",
  "hidden-from-limited-view",
  "neutral",
] as const;
export type SpacesAccess = ElementOf<typeof SpacesAccesses>;
export const isSpacesAccess = isArrayElement(SpacesAccesses);

/**
 * The reason type is so fancy is that RouteObject is a union discriminated by its `index` field, and if we just wrap it with a union straight up, we lose the discrimination.
 * The important part is: if `index` is `true`, there are no `children`. If it's `false` or `undefined`, there _might_ be `children` and those will be more `HTRouteObject`s.
 */
export type HTRouteObject<T extends RRRouteObject = RRRouteObject> = Omit<
  T,
  "children"
> & {
  /**
   * spacesAccess refers to whether the route should be accessible when the user is
   * inside a space. Every route has to declare its spacesAccess. If you're not sure, look at its neighbors or ask.
   *
   * @options
   * - `"accessible-from-limited-view"`: These routes remain accessible when the user is in a space-restricted view.
   * - `"hidden-from-limited-view"`: These routes are blocked/hidden when the user is in a space-restricted view.
   * - `"neutral"`: Routes outside of the workspace scope (e.g. login, onboarding). These routes are not affected by space restrictions.
   */
  spacesAccess: SpacesAccess;
} & (T extends { index: true }
    ? { index: true }
    : { index?: false; children?: HTRouteObject[] });
